import { createAnimation } from '@ionic/angular/standalone'
import type { AnimationBuilder } from '@ionic/angular/standalone'

/**
 * Transition de page de l'application.
 *
 * Aucune des deux transitions fournies par Ionic ne correspond à cette
 * application :
 *
 * - `iosTransitionAnimation` traite `page > ion-header > ion-toolbar` comme une
 *   barre de navigation persistante, mais ne la laisse pas immobile pour
 *   autant : son fond, son titre et ses boutons font un cross-fade
 *   (`enteringToolBarBg`, `enteringToolBarButtons`, `enteringBackButton` :
 *   `opacity` 0.01 -> 1). Sur un header de 100px on voit la page précédente au
 *   travers pendant le fondu. Ces animations ciblent en plus le header par
 *   sélecteurs de descendance directe (`:scope > ion-header > ion-toolbar`),
 *   que le wrapper `<app-header>` empêche de matcher.
 * - `mdTransitionAnimation` déplace la page entière, mais en translation
 *   verticale + fondu.
 *
 * Comportement retenu, décidé par la présence d'un header de part et d'autre :
 *
 * - Les deux pages ont un header : c'est la même barre, elle ne bouge pas du
 *   tout. Aucune animation ne lui est appliquée, seul le reste de la page
 *   glisse dessous.
 * - Une seule des deux pages a un header (map -> account, account -> map) : il
 *   n'y a pas de barre à conserver, le header voyage donc avec sa page.
 *
 * Le geste et les valeurs reprennent la transition iOS d'Ionic.
 */
const DURATION = 540
const EASING = 'cubic-bezier(0.32,0.72,0,1)'

/** Assombrissement de la page qui réapparaît derrière celle qui sort. */
const OFF_OPACITY = 0.8

/** Décalage de la page qui reste en place, pour l'effet de parallaxe. */
const PARALLAX = 33

/**
 * Élément de page qui porte le header, s'il y en a un : soit l'`ion-header`
 * lui-même, soit le wrapper qui le contient (`<app-header>`).
 */
const getPageHeader = (page: HTMLElement): Element | undefined =>
  Array.from(page.children).find(
    (child) =>
      child.tagName === 'ION-HEADER' ||
      child.firstElementChild?.tagName === 'ION-HEADER'
  )

export const pageTransitionAnimation: AnimationBuilder = (_navEl, opts) => {
  const enteringEl: HTMLElement = opts.enteringEl
  const leavingEl: HTMLElement | undefined = opts.leavingEl
  const backDirection = opts.direction === 'back'
  const isRTL = enteringEl.ownerDocument.dir === 'rtl'

  const sign = isRTL ? -1 : 1
  const offScreen = `translateX(${100 * sign}%)`
  const parallax = `translateX(${-PARALLAX * sign}%)`
  const center = 'translateX(0)'

  const enteringHeader = getPageHeader(enteringEl)
  const leavingHeader = leavingEl ? getPageHeader(leavingEl) : undefined
  const keepHeaderFixed = !!enteringHeader && !!leavingHeader

  /**
   * Ce qui se déplace : la page entière quand son header doit voyager avec
   * elle, sinon tout sauf le header — contenu, barre de progression, footer.
   */
  const movingParts = (page: HTMLElement, header: Element | undefined) =>
    keepHeaderFixed
      ? Array.from(page.children).filter((child) => child !== header)
      : [page]

  /**
   * L'animation racine porte sur l'élément de page pour piloter le timing et
   * retirer `ion-page-invisible`, mais ne déplace rien elle-même : sinon le
   * header, qui est dans la page, se déplacerait avec.
   */
  const root = createAnimation()
    .addElement(enteringEl)
    .duration(opts.duration ?? DURATION)
    .easing(opts.easing ?? EASING)
    .fill('both')
    .beforeRemoveClass('ion-page-invisible')

  const entering = createAnimation()
    .addElement(movingParts(enteringEl, enteringHeader))
    .beforeClearStyles(['opacity'])

  if (backDirection) {
    // La page entrante était en dessous : elle remonte de sa position de
    // parallaxe et retrouve son opacité pleine.
    entering
      .fromTo('transform', parallax, center)
      .fromTo('opacity', OFF_OPACITY, 1)
  } else {
    entering.fromTo('transform', offScreen, center)
  }
  root.addAnimation(entering)

  if (leavingEl) {
    const leaving = createAnimation()
      .addElement(movingParts(leavingEl, leavingHeader))
      .beforeClearStyles(['opacity'])

    if (backDirection) {
      leaving.fromTo('transform', center, offScreen)
    } else {
      // Pas d'animation d'opacité ici : la page sortante recouvre la page
      // entrante, la rendre translucide la laisserait apparaître au travers.
      leaving.fromTo('transform', center, parallax)
    }
    root.addAnimation(leaving)
  }

  return root
}
